'use client';

import { cx } from 'class-variance-authority';
import {
    FormGroup,
    FormInputErrors,
    Icon,
    Input,
    Textarea,
    MaskShapes,
} from '~/components/primitives';
import { z } from 'zod';
import useFormState, { createFormValidation } from 'use-form-state';
import posthog from 'posthog-js';
import StoryblokImage from '~/storyblok/utils/StoryblokImage';
import RichText from '~/storyblok/utils/RichText';
import { useI18n } from '~/features/i18n';
import { GTM } from '~/features/google/tagmanager';
import Title from '~/components/utils/Title';
import { QuestionForm } from './ContactFormSection/client';
import { storyblokBridgeAttributes, type StoryblokBlokProps } from './utils';
import type { SB_EventContactFormSectionProps } from './types.generated';
import Alert from '~/components/layout/Alert';

export const EventContactFormSection: React.FC<
    StoryblokBlokProps<SB_EventContactFormSectionProps>
> = props => {
    const i18n = useI18n();
    const formState = useEventContactFormSectionFormState({});
    const { getNativeInputProps, getErrorMessages, handleChange } = formState;

    const sendEmail = QuestionForm.useSendMailMutation(i18n, () => {
        GTM.setUserData(formState.values.email);
        GTM.sendEvent('funkeyEventFormSubmit', {
            ...formState.values,
        });
        posthog.capture('funkeyEventFormSubmit', formState.values);
        formState.resetForm();
    });

    return (
        <section
            {...storyblokBridgeAttributes(props.blok)}
            id={props.blok.id}
            className="bg-secondary pb-20 pt-28"
        >
            <div className="container flex flex-col items-center lg:flex-row lg:gap-20">
                <div className="flex flex-col">
                    <div className="mb-10 flex flex-col gap-8">
                        <Title size="3xl" className="text-left" color="light">
                            {props.blok.title}
                        </Title>

                        {props.blok.text && <RichText text={props.blok.text} textColor="white" />}
                        {sendEmail.isSuccess && !!sendEmail.data?.sendEmail?.success && (
                            <Alert variant="info">{i18n._('Mail send successfully')}</Alert>
                        )}
                    </div>

                    <div className="mt-4 flex w-full flex-col gap-2 lg:flex-row lg:gap-6">
                        <FormGroup
                            label={i18n._('Full Name')}
                            htmlFor="fullName"
                            className="mt-1 w-full"
                            labelTextColor="text-white"
                        >
                            <Input id="fullName" {...getNativeInputProps('fullName')} />
                            <FormInputErrors errors={getErrorMessages('fullName')} />
                        </FormGroup>

                        <FormGroup
                            label={i18n._('Phonenumber')}
                            htmlFor="phonenumber"
                            className="mt-1 w-full"
                            labelTextColor="text-white"
                        >
                            <Input id="phonenumber" {...getNativeInputProps('phonenumber')} />
                            <FormInputErrors errors={getErrorMessages('phonenumber')} />
                        </FormGroup>
                    </div>

                    <div className="mt-2 flex w-full flex-col gap-2 lg:mt-4 lg:flex-row lg:gap-6">
                        <FormGroup
                            label={i18n._('Email')}
                            htmlFor="email"
                            className="mt-1 w-full"
                            labelTextColor="text-white"
                        >
                            <Input id="email" {...getNativeInputProps('email')} />
                            <FormInputErrors errors={getErrorMessages('email')} />
                        </FormGroup>

                        <FormGroup
                            label={i18n._('Company')}
                            htmlFor="company"
                            className="mt-1 w-full"
                            labelTextColor="text-white"
                        >
                            <Input id="company" {...getNativeInputProps('company')} />
                            <FormInputErrors errors={getErrorMessages('company')} />
                        </FormGroup>
                    </div>

                    <div className="mt-2 lg:mt-4">
                        <FormGroup
                            label={i18n._('Tell us your event idea')}
                            htmlFor="idea"
                            className="mt-1 w-full"
                            labelTextColor="text-white"
                        >
                            <Textarea
                                className="mt-1"
                                rows={6}
                                value={formState.values.idea}
                                onChange={e => handleChange('idea', e.currentTarget.value)}
                            />
                            <FormInputErrors errors={getErrorMessages('idea')} />
                        </FormGroup>
                    </div>
                    <div className="mb-20 mt-10">
                        <button
                            onClick={event => {
                                event.preventDefault();
                                if (!formState.validate()) return;

                                sendEmail.mutateAsync(
                                    {
                                        firstName: formState.values.fullName,
                                        fromEmail: 'info@funkey.be',
                                        subject: 'Event contact form',
                                    },
                                    {
                                        [i18n._('Full Name')]: formState.values.fullName,
                                        [i18n._('Phonenumber')]: formState.values.phonenumber,
                                        [i18n._('Email')]: formState.values.email,
                                        [i18n._('Company')]: formState.values.company,
                                        [i18n._('Tell us your event idea')]: formState.values.idea,
                                    },
                                );
                            }}
                            type="submit"
                            className="flex gap-4 rounded-full bg-tertiary px-4 py-3 font-bold text-white transition-all hover:bg-tertiary-600 md:px-8 md:py-6"
                        >
                            <Icon name="arrow-right" aria-hidden="true" />
                            {i18n._('Contact us')}
                        </button>
                    </div>
                </div>
                {props.blok.asset?.filename && (
                    <>
                        <StoryblokImage
                            src={props.blok.asset.filename}
                            alt={props.blok.asset.alt || props.blok.asset.filename}
                            fill={false}
                            maskType={props.blok.maskType as any}
                            className={cx('h-full w-full transform object-cover lg:w-1/3')}
                        />
                        {props.blok.maskType && (
                            <MaskShapes maskType={props.blok.maskType as any} />
                        )}
                    </>
                )}
            </div>
        </section>
    );
};

const NAME_VALIDATOR = z.string().min(1).max(256).trim();

const useEventContactFormSectionFormState = (initialValues: any) => {
    const i18n = useI18n();

    const validation = createFormValidation([
        {
            path: 'fullName',
            validate: value => NAME_VALIDATOR.safeParse(value).success,
            message: i18n._('Fullname required and must be between {min} and {max} characters', {
                min: NAME_VALIDATOR.minLength,
                max: NAME_VALIDATOR.maxLength,
            }),
        },
        {
            path: 'phonenumber',
            validate: value => z.string().min(8).max(20).safeParse(value).success,
            message: i18n._('Invalid phonenumber'),
        },
        {
            path: 'email',
            validate: value => z.email().safeParse(value).success,
            message: i18n._('Invalid email'),
        },
        {
            path: 'company',
            validate: value => z.string().min(2).max(255).safeParse(value).success,
            message: i18n._('Company required and must be between {min} and {max} characters', {
                min: 2,
                max: 255,
            }),
        },
        {
            path: 'idea',
            validate: value => z.string().min(1).safeParse(value).success,
            message: i18n._('Idea required and must contain {min} character', {
                min: 1,
            }),
        },
    ]);

    return useFormState(
        {
            fullName: '',
            phonenumber: '',
            email: '',
            company: '',
            idea: '',
            ...initialValues,
        },
        {
            validation,
            valuesToInput: ({ fullName, phonenumber, email, company, idea }) => ({
                fullName,
                phonenumber,
                email,
                company,
                idea,
            }),
        },
    );
};
